import React from 'react';
import { Box } from '@mui/joy';
import { FloatingToolbar } from './FloatingToolbar';
import type { SectionOverlayProps, SectionBounds } from '../../types/visualEditor';

interface SectionOverlayComponentProps extends Omit<SectionOverlayProps, 'bounds'> {
  bounds: SectionBounds;
  iframeScrollTop: number;
}

export const SectionOverlay: React.FC<SectionOverlayComponentProps> = ({
  index,
  section,
  sectionConfig,
  bounds,
  iframeScrollTop,
  isSelected,
  isHovered,
  isDragging,
  isDragOver,
  onSelect,
  onStartDrag,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onEdit,
  canMoveUp,
  canMoveDown,
}) => {
  // Calculate position accounting for iframe scroll
  const top = bounds.top - iframeScrollTop;
  const isVisible = top + bounds.height > 0 && top < window.innerHeight;

  if (!isVisible && !isSelected) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: top,
        left: bounds.left,
        width: bounds.width,
        height: bounds.height,
        pointerEvents: 'none',
        zIndex: isSelected ? 10 : isDragging ? 5 : 1,
        transition: isDragging ? 'none' : 'opacity 0.15s ease',
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {/* Selection/Hover border with background tint and glow */}
      <Box
        sx={{
          position: 'absolute',
          inset: -2,
          border: isSelected
            ? '2px solid #2563eb'
            : isHovered
            ? '2px solid #60a5fa'
            : isDragOver
            ? '2px dashed #10b981'
            : 'none',
          borderRadius: '6px',
          pointerEvents: 'none',
          transition: 'all 0.2s ease',
          // Subtle background tint on hover/selection
          backgroundColor: isSelected
            ? 'rgba(37, 99, 235, 0.06)'
            : isHovered
            ? 'rgba(96, 165, 250, 0.04)'
            : 'transparent',
          // Glow effect for better visibility
          boxShadow: isSelected
            ? '0 0 0 4px rgba(37, 99, 235, 0.12), 0 4px 12px rgba(37, 99, 235, 0.15)'
            : isHovered
            ? '0 0 0 3px rgba(96, 165, 250, 0.1)'
            : 'none',
        }}
      />

      {/* Drag handle - always visible when selected or hovered */}
      {(isSelected || isHovered) && !isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'translate(-50%, -50%)',
            width: 24,
            height: 24,
            bgcolor: isSelected ? 'primary.500' : 'neutral.500',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            pointerEvents: 'auto',
            boxShadow: 'sm',
            '&:hover': {
              bgcolor: 'var(--brand-text, #7013f5)',
            },
            '&:active': {
              cursor: 'grabbing',
            },
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onStartDrag();
          }}
        >
          {/* Drag icon */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="white"
          >
            <path d="M8 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM8 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM14 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
          </svg>
        </Box>
      )}

      {/* Section type label */}
      {(isSelected || isHovered) && !isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: -28,
            left: 24,
            bgcolor: isSelected ? 'primary.500' : 'neutral.600',
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
            pointerEvents: 'none',
            boxShadow: 'sm',
            whiteSpace: 'nowrap',
          }}
        >
          {sectionConfig?.label || section.type}
          {section.variant && section.variant !== 'default' && (
            <span style={{ opacity: 0.8, marginLeft: 4 }}>• {section.variant}</span>
          )}
        </Box>
      )}

      {/* Floating toolbar - only when selected */}
      {isSelected && !isDragging && (
        <FloatingToolbar
          section={section}
          sectionConfig={sectionConfig}
          bounds={bounds}
          onEdit={onEdit}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onAIGenerate={() => {
            // Open section editor with AI tab
            onEdit();
          }}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
        />
      )}

      {/* Clickable area for selection */}
      {!isSelected && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            cursor: 'pointer',
            pointerEvents: 'auto',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          onMouseEnter={() => {
            // Hover is handled by iframe PostMessage
          }}
        />
      )}
    </Box>
  );
};
